import {html, LitElement, PropertyValues} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {defaultSettings, Settings} from './models/fishman-forgot-password.model';
import {a11Fetch, A11SiteDataModel, FetchProps} from './utilities';

@customElement('fishman-register')
export class FishmanRegister extends LitElement {

    @property({type: String})
    encodedSettings = '';

    @property({type: Object})
    settings: Settings = {...defaultSettings};

    @property({type: String})
    current_user_name = '';

    @property({type: String})
    default_username_value = '';

    @property({type: Boolean})
    is_form_submitting = false;

    @property({type: Boolean})
    is_successful = false;

    @property({type: String})
    error_message = '';

    override firstUpdated = () => {
        this.current_user_name = this.default_username_value;
    }

    override updated(changedProperties: PropertyValues) {
        if (!changedProperties.has('encodedSettings')) {
            return;
        }

        try {
            const rawSettings = atob(this.encodedSettings); // decode base64 string
            this.settings = JSON.parse(rawSettings); // parse the JSON string into an object
        } catch (e) {
            console.error('Cannot parse settings:', e);
        }
    }

    getPasswordRequirements = () => {
        const password_length = 8;
        const password_requirements = ['uppercase', 'lowercase', 'number'];
        let password_regex = `^`;
        let error_message = ``;
        const password_length_min = password_length && password_length < 4 ? 4 : password_length;

        error_message = password_requirements && password_requirements.length > 0 ? `Password must contain ` : ``;
        if (password_requirements && password_requirements.includes('uppercase')) {
            password_regex += `(?=.*[A-Z])`;
            error_message += `at least one uppercase letter, `;
        }
        if (password_requirements && password_requirements.includes('lowercase')) {
            password_regex += `(?=.*[a-z])`;
            error_message += `at least one lowercase letter, `;
        }
        if (password_requirements && password_requirements.includes('number')) {
            password_regex += `(?=.*[0-9])`;
            error_message += `at least one number, `;
        }
        if (password_requirements && password_requirements.includes('special_character')) {
            password_regex += `(?=.*[\\!@#\\$%\\^&\\*\\(\\)\\-\\+\\?~])`;
            error_message += `at least one special character (!@#$%^&*()-+?~), `;
        }
        password_regex += `.{${password_length_min},}$`;
        if (password_requirements && password_requirements.length > 0) {
            error_message = error_message.slice(0, -2) + `. `;
        }
        error_message += `Password must be at least ${password_length} characters long.`;

        return {password_regex, password_length, password_requirements, error_message};
    }

    override render() {

        const {error_message, password_regex, password_length, password_requirements} = this.getPasswordRequirements();

        if (this.is_successful) {
            return html`
                <div class="alert alert-success my-5 mb-2" role="alert">
                    Your account has been created successfully. Your email address must be verified before proceeding.
                </div>

                <fishman-verify-email
                        .encodedSettings=${this.encodedSettings}
                        .default_email_value=${this.default_username_value}
                        @fishman-login-return-to-login=${this._handleReturnToLoginClick}
                ></fishman-verify-email>
            `;
        }

        return html`
            <form class="row py-3 g-3" @submit=${this.onSubmit}>
                <div class="col-12 d-flex flex-column gap-3">
                    <h1 class="fishman-forgot-password__section-title text-center">
                        Create account
                    </h1>

                    <div>
                        <label for="username" class="form-label">Email address</label>
                        <input type="text" name="username" id="username" class="form-control"
                               .value=${this.default_username_value} @change=${this._handleUserNameChange}
                               data-test="username" required>
                    </div>

                    <div class="fishman-register__row">
                        <div class="fishman-register__column">
                            <div>
                                <label for="register-first-name">First Name</label>
                            </div>
                            <div>
                                <input type="text" name="first_name" id="register-first-name"
                                       class="form-control a11-input"
                                       autofocus
                                       required/>
                            </div>
                        </div>

                        <div class="fishman-register__column">
                            <div>
                                <label for="register-last-name">Last Name</label>
                            </div>
                            <div>
                                <input type="text" name="last_name" id="register-last-name"
                                       class="form-control a11-input"
                                       required/>
                            </div>
                        </div>
                    </div>

                    <div class="col-sm-12 col-md-6">
                        <div>
                            <label for="new-password">Password</label>
                        </div>
                        <div>
                            <input type="password" name="password" id="new-password"
                                   class="form-control a11-input"
                                   pattern="${password_regex}" title="${error_message}" autocomplete="off"
                                   required/>
                        </div>
                    </div>

                    <div class="alert alert-info mb-0" role="alert" data-test="fishman-update-password-info">
                        Your password must include:
                        <ul class="mb-0">
                            ${password_length ? html`
                                <li>At least ${password_length} characters</li>` : ''}
                            ${password_requirements?.map(req => html`
                                ${req === 'uppercase' ? html`
                                    <li>At least one uppercase letter</li>` : ''}
                                ${req === 'lowercase' ? html`
                                    <li>At least one lowercase letter</li>` : ''}
                                ${req === 'number' ? html`
                                    <li>At least one number</li>` : ''}
                                ${req === 'special_character' ? html`
                                    <li>At least one special character (!@#$%^&*()-+?~)</li>` : ''}
                            `)}
                        </ul>
                    </div>

                    ${this.is_form_submitting ? html`
                        <div class="alert alert-info mb-0" role="alert"
                             data-test="fishman-forgot-password-validating-details">Creating your account...
                        </div>
                    ` : ''}

                    ${this.error_message !== '' ? html`
                        <div class="alert alert-danger mb-0" role="alert"
                             data-test="fishman-forgot-password-failed-invalid-information"
                             .innerHTML=${this.error_message}/>
                    ` : ''}

                    <p>
                        <button type="submit" class="btn btn-primary align-self-center"
                                ?disabled=${this.is_form_submitting}
                                data-test="submit">
                            Create Account
                        </button>
                    </p>

                    <p>
                        <button class="btn btn-primary-outline" type="button" @click=${this._handleReturnToLoginClick}
                                data-test="fishman-login-return-to-login">Cancel
                        </button>
                    </p>

                </div>
            </form>
        `;
    }

    override createRenderRoot() {
        return this;
    }

    /**
     * Triggers a custom event to return the user to the login screen. The fishman-login component listens for this event and
     * will output the fishman-login component when this is fired. The fishman-login component also uses the current username value
     * so, it can populate the username field with what had been entered in the fishman-forgot-password component.
     *
     */
    private _handleReturnToLoginClick = () => {
        const event = new CustomEvent('fishman-login-return-to-login', {detail: {current_user_name: this.current_user_name}});
        this.dispatchEvent(event);
    }

    /**
     * Keeps the value of the username field in a property in the state. This allows the 'return to login' button to know
     * what the value of the username field is, and send that value back to the fishman-login component.
     *
     * @param {Event} e - The event object.
     */
    private _handleUserNameChange = (e: Event) => {
        this.current_user_name = (e.target as HTMLInputElement).value;
    }

    private async onSubmit(e: Event) {
        e.preventDefault();
        this.is_form_submitting = true;
        this.error_message = '';

        const {block_id} = this.settings;

        const form = e.target as HTMLFormElement;
        const formData = new FormData(form);

        const fetchProperties: FetchProps = {
            action: 'fishman_register_user_via_ajax',
            block_id,
            method: 'POST',
            body: formData,
        };

        const {statusCode, errorMessage} = await a11Fetch(fetchProperties);

        if (statusCode === 200) {
            this.is_successful = true;
            return;
        }

        this.error_message = errorMessage;
        this.is_form_submitting = false;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fishman-register': FishmanRegister;
    }

    interface Window {
        ai_site_data: A11SiteDataModel
    }
}
